import {
  BAR_COLOR_PRESETS,
  FONT_FAMILY_OPTIONS,
  FONT_SIZE_OPTIONS,
  TEXT_ALIGN_OPTIONS,
  TEXT_COLOR_PRESETS,
} from "constants/siteHeaderMessageStyle";

function ColorField({ label, value, onChange, presets }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-semibold text-gray-700 dark:text-gray-300">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={
            presets.some((p) => p.value && p.value === value)
              ? value
              : value
                ? "__custom__"
                : ""
          }
          onChange={(e) => {
            const v = e.target.value;
            if (v === "__custom__") return;
            onChange(v);
          }}
          className="min-w-[140px] flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
        >
          {presets.map((p) => (
            <option key={p.label} value={p.value}>
              {p.label}
            </option>
          ))}
          <option value="__custom__">Custom…</option>
        </select>
        <input
          type="color"
          value={
            /^#[0-9A-Fa-f]{6}$/.test(value || "")
              ? value
              : "#4318FF"
          }
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-10 w-12 cursor-pointer rounded-lg border border-gray-200 bg-white p-1 dark:border-white/10"
          title="Pick a custom color"
        />
        {value ? (
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-xs font-medium text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Reset
          </button>
        ) : null}
      </div>
      {value ? (
        <span className="mt-1 block text-xs text-gray-500">{value}</span>
      ) : null}
    </label>
  );
}

/**
 * @param {{ style: object, onChange: (patch: object) => void }} props
 */
export default function MessageStyleFields({ style, onChange }) {
  const patch = (p) => onChange({ ...style, ...p });

  return (
    <div className="space-y-4 rounded-xl border border-dashed border-gray-300 bg-gray-50/80 p-4 dark:border-white/15 dark:bg-navy-900/40">
      <h3 className="text-sm font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
        Banner style
      </h3>

      <div className="grid gap-4 md:grid-cols-2">
        <ColorField
          label="Bar color"
          value={style.barColor || ""}
          onChange={(barColor) => patch({ barColor })}
          presets={BAR_COLOR_PRESETS}
        />
        <ColorField
          label="Text color"
          value={style.textColor || ""}
          onChange={(textColor) => patch({ textColor })}
          presets={TEXT_COLOR_PRESETS}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <label className="block text-sm">
          <span className="mb-1 block font-semibold text-gray-700 dark:text-gray-300">
            Font
          </span>
          <select
            value={style.fontFamily || "default"}
            onChange={(e) => patch({ fontFamily: e.target.value })}
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
          >
            {FONT_FAMILY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block font-semibold text-gray-700 dark:text-gray-300">
            Font size
          </span>
          <select
            value={style.fontSizePx || ""}
            onChange={(e) => patch({ fontSizePx: e.target.value })}
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
          >
            {FONT_SIZE_OPTIONS.map((o) => (
              <option key={o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block font-semibold text-gray-700 dark:text-gray-300">
            Alignment
          </span>
          <select
            value={style.textAlign || "center"}
            onChange={(e) => patch({ textAlign: e.target.value })}
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900 dark:text-white"
          >
            {TEXT_ALIGN_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
        <input
          type="checkbox"
          checked={Boolean(style.fontItalic)}
          onChange={(e) => patch({ fontItalic: e.target.checked })}
        />
        Italic text
      </label>
    </div>
  );
}
